import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  getMyNotifications,
  getUnreadNotificationCount,
  markNotificationRead,
  markAllNotificationsRead,
  completeNotification,
  reopenNotification
} from 'lib/supabaseQueries';
import { useAuthContext } from 'context/authContext';

// System-generated alerts for the navbar bell. No realtime in the app yet,
// so both queries poll - same approach as the reminders count.
const POLL_INTERVAL = 60 * 1000;

export function useNotifications(limit = 20, completed = false) {
  const { isInitialized } = useAuthContext();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['notifications', limit, completed],
    queryFn: () => getMyNotifications(limit, completed),
    enabled: isInitialized,
    // Completed alerts are a history view - only fetched when opened, and no
    // need to poll them.
    refetchInterval: completed ? false : POLL_INTERVAL
  });

  return {
    notifications: data?.data || [],
    isLoading,
    error: error || data?.error,
    refetch
  };
}

export function useUnreadNotificationCount() {
  const { isInitialized } = useAuthContext();

  const { data } = useQuery({
    queryKey: ['unreadNotificationCount'],
    queryFn: () => getUnreadNotificationCount(),
    enabled: isInitialized,
    refetchInterval: POLL_INTERVAL
  });

  return { unreadCount: data?.data || 0 };
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: notificationId => markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
    },
    onError: error => {
      console.error('Mark notification read error:', error);
    }
  });

  return {
    markRead: mutation.mutate,
    isMarking: mutation.isPending
  };
}

export function useCompleteNotification() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ notificationId, notes }) =>
      completeNotification(notificationId, notes),
    onSuccess: result => {
      if (result?.error) throw new Error(result.error);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
      toast.success('Alert marked as completed');
    },
    onError: error => {
      console.error('Complete notification error:', error);
      toast.error('Failed to complete alert');
    }
  });

  return {
    completeAlert: mutation.mutate,
    completeAlertAsync: mutation.mutateAsync,
    isCompleting: mutation.isPending
  };
}

export function useReopenNotification() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ notificationId, notes }) =>
      reopenNotification(notificationId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
      toast.success('Alert reopened');
    },
    onError: error => {
      console.error('Reopen notification error:', error);
      toast.error('Failed to reopen alert');
    }
  });

  return {
    reopenAlert: mutation.mutate,
    isReopening: mutation.isPending
  };
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unreadNotificationCount'] });
    },
    onError: error => {
      console.error('Mark all notifications read error:', error);
    }
  });

  return {
    markAllRead: mutation.mutate,
    isMarkingAll: mutation.isPending
  };
}
